import {
  QuerySupplierInsuranceOptionsOrderByColumn,
  SortOrder,
  SupplierInsuranceOptionListQueryVariables,
} from "@/gql/graphql";
import { formatCurrency } from "@/lib/Formatters/formatCurrency";
import {
  SupplierInsuranceOptionListItem,
  supplierInsuranceOptionListQuery,
} from "@/app/Suppliers/GraphQL/supplierInsuranceOptionListQuery";
import { SupplierRecord } from "@/app/Suppliers/GraphQL/supplierRecordQuery";
import { useSupplierInsuranceOptionActions } from "@/app/Suppliers/Hooks/useSupplierInsuranceOptionActions";
import {
  DataTable,
  DataTableColDef,
} from "@/lib/Components/DataTable/DataTable";
import type { TableId } from "@/app/Common/Utils/table-id";

import { ReactNode } from "react";
import { Badge } from "@/components/catalyst/badge";
import { useTranslations } from "use-intl";

type SupplierInsuranceOptionTableProps = {
  queryVariables?: Partial<SupplierInsuranceOptionListQueryVariables>;
  supplier: SupplierRecord;
  id: TableId;
  columns?: DataTableColDef<
    SupplierInsuranceOptionListItem,
    unknown,
    SupplierInsuranceTableId
  >[];
  rightButtons?: ReactNode;
  title?: string;
};

export type SupplierInsuranceTableId =
  | "name"
  | "badges"
  | "bond"
  | "standard_liability";
export function SupplierInsuranceOptionTable({
  id,
  queryVariables,
  supplier,
  columns,
  rightButtons,
  title,
}: SupplierInsuranceOptionTableProps) {
  const getActions = useSupplierInsuranceOptionActions();
  const t = useTranslations("supplier.insurance");

  const defaultColumns: DataTableColDef<
    SupplierInsuranceOptionListItem,
    unknown,
    SupplierInsuranceTableId
  >[] = [
    {
      id: "name",
      header: t("name"),
      accessorFn: (row) => row.name,
    },
    {
      id: "badges",
      header: "",
      cell: ({ row }) =>
        row.original.is_default ? <Badge>{t("primary")}</Badge> : null,
    },
    {
      id: "bond",
      header: t("bond"),
      accessorFn: (row) => formatCurrency(row.bond_amount, supplier.currency),
    },
    {
      id: "standard_liability",
      header: t("standardLiability"),
      accessorFn: (row) =>
        formatCurrency(row.standard_liability_amount, supplier.currency),
    },
  ];

  return (
    <DataTable
      id={id}
      title={title}
      getActions={getActions}
      document={supplierInsuranceOptionListQuery}
      accessor={(data) => data.supplierInsuranceOptions}
      getQueryVariables={({ pagination }) => {
        return {
          first: pagination.pageSize,
          page: pagination.pageIndex,
          supplierId: supplier.id,
          orderBy: [
            {
              column: QuerySupplierInsuranceOptionsOrderByColumn.Id,
              order: SortOrder.Asc,
            },
          ],
          ...queryVariables,
        };
      }}
      columns={columns ? [...defaultColumns, ...columns] : defaultColumns}
      rightButtons={rightButtons}
    />
  );
}
